import React from 'react'
import {useExperiments} from '@wix/yoshi-flow-editor'

import {
  ReserveReservationApiError,
  ReserveReservationApplicationErrorCode,
} from '../../actions/reserveReservation'
import {ErrorModal} from '../../../../components-shared/ErrorModal'
import {EXPERIMENTS} from '../../../../utils/constants'
import {isResolvedError} from '../../../../utils/errors'

import {useHooks} from './useHooks'

// remove after specs.tableReservations.errorHandlerIntegrationLiveSite is merged
const isReserveReservationApiError = (error): error is ReserveReservationApiError => {
  return 'applicationCode' in error
}

interface Texts {
  title: string
  text: string
  ctaButton: string
}

const DEFAULT_TEXTS: Texts = {
  title: 'uou-reservations.reservation-details.error-modal.title',
  text: 'uou-reservations.reservation-details.error-modal.text',
  ctaButton: 'uou-reservations.reservation-details.error-modal.cta',
}

const APPLICATION_ERROR_CODE_TO_TEXTS: Record<ReserveReservationApplicationErrorCode, Texts> = {
  [ReserveReservationApplicationErrorCode.HELD_EXPIRED]: {
    title: 'uou-reservations.reservation-details.held-modal.title',
    text: 'uou-reservations.reservation-details.held-expired-error',
    ctaButton: 'uou-reservations.reservation-details.held-modal.cta',
  },
  [ReserveReservationApplicationErrorCode.TOO_MANY_REQUESTS]: {
    title: 'uou-reservations.reservation-details.too-many-requests-modal.title',
    text: 'uou-reservations.reservation-details.too-many-requests-error',
    ctaButton: 'uou-reservations.reservation-details.too-many-requests-modal.cta',
  },
}

export const ReservationErrorModal: React.FC = () => {
  const {isModalOpen, onClose, onCtaButtonClick, apiRequestErrorDetails} = useHooks()
  const {experiments} = useExperiments()
  const isErrorHandlerIntegrationEnabled = experiments.enabled(EXPERIMENTS.errorHandlerIntegration)

  if (!apiRequestErrorDetails) {
    return null
  }

  const getErrorTexts = () => {
    if (isErrorHandlerIntegrationEnabled && isResolvedError(apiRequestErrorDetails)) {
      if (apiRequestErrorDetails.applicationError?.code) {
        return {
          title:
            APPLICATION_ERROR_CODE_TO_TEXTS[apiRequestErrorDetails.applicationError.code]?.title ??
            DEFAULT_TEXTS.title,
          text: apiRequestErrorDetails?.message || DEFAULT_TEXTS.text,
          ctaButton:
            APPLICATION_ERROR_CODE_TO_TEXTS[apiRequestErrorDetails.applicationError.code]
              ?.ctaButton ?? DEFAULT_TEXTS.ctaButton,
        }
      }
    }

    // remove after specs.tableReservations.errorHandlerIntegrationLiveSite is merged
    if (
      isReserveReservationApiError(apiRequestErrorDetails) &&
      apiRequestErrorDetails.applicationCode
    ) {
      return (
        APPLICATION_ERROR_CODE_TO_TEXTS[apiRequestErrorDetails.applicationCode] ?? DEFAULT_TEXTS
      )
    }

    return DEFAULT_TEXTS
  }

  const {title, text, ctaButton} = getErrorTexts()

  return (
    <ErrorModal
      isModalOpen={isModalOpen}
      title={title}
      text={text}
      ctaButton={ctaButton}
      onClose={onClose}
      onCtaButtonClick={onCtaButtonClick}
    />
  )
}
